import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { money, toCents, fmtDate } from '../money';
import {
  ArrowLeft, Search, Download, CheckCircle2, AlertTriangle, Shuffle, FileText,
  Paperclip, Tag, Palette,
} from 'lucide-react';

const CARD = 'bg-white text-slate-800 border-slate-200';
const field = 'h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm text-slate-800';
const errOf = (e) => e?.response?.data?.detail || 'Request failed';
const Head = ({ title, sub, onBack, right }) => (
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-start gap-2">
      {onBack && <button onClick={onBack} className="mt-0.5 text-slate-500 hover:text-slate-800" data-testid="batch3-back"><ArrowLeft className="h-5 w-5" /></button>}
      <div><h2 className="text-xl font-semibold text-slate-800">{title}</h2>{sub && <p className="text-sm text-slate-500">{sub}</p>}</div>
    </div>
    {right}
  </div>
);

// ── Reclassify transactions ──────────────────────────────────────────────────
export function ReclassifyPage({ base, onBack }) {
  const [accts, setAccts] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { axios.get(`${base}/accounts`).then((r) => setAccts((r.data?.accounts || []).filter((a) => a.type && !['Bank', 'Credit Card'].includes(a.type) === false || true))).catch(() => {}); }, [base]);
  const postable = accts.filter((a) => !a.no_post);
  const send = async (commit) => {
    if (!from || !to) { setErr('Pick a source and a target account.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${base}/reclassify-batch`, { from_account_id: from, to_account_id: to, date_from: start || null, date_to: end || null, commit });
      if (commit) { setDone(r.data); setPreview(null); } else setPreview(r.data);
    } catch (e) { setErr(errOf(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="reclassify-page">
      <Head title="Reclassify transactions" onBack={onBack}
        sub="Move every posted line from one account to another over an optional date range. Preview first; closed periods are protected." />
      {err && <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="reclassify-err">{err}</div>}
      <div className={`rounded-lg border p-4 ${CARD} max-w-2xl space-y-3`}>
        <label className="grid sm:grid-cols-[160px_1fr] items-center gap-2"><span className="text-sm text-slate-600">From account</span>
          <select className={field} value={from} onChange={(e) => { setFrom(e.target.value); setPreview(null); setDone(null); }} data-testid="reclassify-from">
            <option value="">Select…</option>{accts.map((a) => <option key={a.account_id} value={a.account_id}>{a.full_name || a.display_name}</option>)}</select></label>
        <label className="grid sm:grid-cols-[160px_1fr] items-center gap-2"><span className="text-sm text-slate-600">Change account to</span>
          <select className={field} value={to} onChange={(e) => { setTo(e.target.value); setPreview(null); setDone(null); }} data-testid="reclassify-to">
            <option value="">Select…</option>{postable.map((a) => <option key={a.account_id} value={a.account_id}>{a.full_name || a.display_name}</option>)}</select></label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-500">From date<Input type="date" className={field} value={start} onChange={(e) => { setStart(e.target.value); setPreview(null); }} data-testid="reclassify-start" /></label>
          <label className="text-xs text-slate-500">To date<Input type="date" className={field} value={end} onChange={(e) => { setEnd(e.target.value); setPreview(null); }} data-testid="reclassify-end" /></label>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => send(false)} disabled={busy || !from || !to} data-testid="reclassify-preview"><Shuffle className="h-4 w-4 mr-1" />{busy ? 'Working…' : 'Preview'}</Button>
          <Button onClick={() => send(true)} disabled={busy || !preview} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reclassify-apply"><CheckCircle2 className="h-4 w-4 mr-1" />Reclassify {preview?.count ?? ''} lines</Button>
        </div>
      </div>
      {done && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="reclassify-done"><CheckCircle2 className="h-4 w-4 inline mr-1" />Reclassified {done.count} lines from {done.from} to {done.to}.</div>}
      {preview && (
        <div className={`rounded-lg border p-4 ${CARD} max-w-2xl`} data-testid="reclassify-preview-result">
          <div className="text-sm mb-2 text-amber-700"><AlertTriangle className="h-4 w-4 inline mr-1" />Preview only — {preview.count} lines from <b>{preview.from}</b> → <b>{preview.to}</b>.</div>
          <table className="w-full text-sm"><tbody>
            {(preview.sample || []).map((s, i) => (
              <tr key={i} className="border-b border-slate-100"><td className="py-1">{fmtDate(s.date)}</td><td className="py-1 text-right tabular-nums">{money(s.amount)}</td></tr>
            ))}
          </tbody></table>
        </div>
      )}
    </div>
  );
}

// ── Advanced transactions search (full page) ──────────────────────────────────
const TXN_TYPES = ['All', 'Invoice', 'Pledge', 'Payment', 'Sales Receipt', 'Deposit', 'Check', 'Expense', 'Bill', 'Bill Payment', 'Journal Entry'];
export function AdvancedSearchPage({ base, go, onBack, initialQuery = '' }) {
  const [q, setQ] = useState(initialQuery);
  const [type, setType] = useState('All');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [name, setName] = useState('');
  const [num, setNum] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const terms = [q, name, num].filter(Boolean).join(' ') || q;
      const r = await axios.get(`${base}/search`, { params: { q: terms || q || ' ' } });
      let rows = r.data?.transactions || [];
      if (type !== 'All') rows = rows.filter((t) => (t.type || '').toLowerCase() === type.toLowerCase());
      if (start) rows = rows.filter((t) => (t.date || '') >= start);
      if (end) rows = rows.filter((t) => (t.date || '') <= end);
      if (num) rows = rows.filter((t) => String(t.num || '').toLowerCase().includes(num.toLowerCase()));
      if (name) rows = rows.filter((t) => (t.name || '').toLowerCase().includes(name.toLowerCase()));
      setRes({ ...r.data, transactions: rows });
    } finally { setBusy(false); }
  };
  useEffect(() => { if (initialQuery) run(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4" data-testid="advanced-search-page">
      <Head title="Advanced transactions search" onBack={onBack} sub="Filter transactions by type, date range, amount, name and number." />
      <div className={`rounded-lg border p-4 ${CARD} grid sm:grid-cols-3 gap-3`}>
        <label className="text-xs text-slate-500 sm:col-span-3">Search<Input className={field} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Doc number, memo, amount, name…" data-testid="advsearch-q" /></label>
        <label className="text-xs text-slate-500">Type<select className={field} value={type} onChange={(e) => setType(e.target.value)} data-testid="advsearch-type">{TXN_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
        <label className="text-xs text-slate-500">From date<Input type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} data-testid="advsearch-start" /></label>
        <label className="text-xs text-slate-500">To date<Input type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="advsearch-end" /></label>
        <label className="text-xs text-slate-500">Name<Input className={field} value={name} onChange={(e) => setName(e.target.value)} data-testid="advsearch-name" /></label>
        <label className="text-xs text-slate-500">Number<Input className={field} value={num} onChange={(e) => setNum(e.target.value)} data-testid="advsearch-num" /></label>
        <div className="flex items-end"><Button onClick={run} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="advsearch-run"><Search className="h-4 w-4 mr-1" />{busy ? 'Searching…' : 'Search'}</Button></div>
      </div>
      {res && (
        <div className={`rounded-lg border overflow-hidden ${CARD}`} data-testid="advsearch-results">
          <table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="p-2">Type</th><th className="p-2">No.</th><th className="p-2">Name</th><th className="p-2">Date</th><th className="p-2 text-right">Amount</th><th className="p-2">Status</th></tr></thead>
            <tbody>
              {res.transactions.map((t) => (
                <tr key={t.txn_id} className="border-b border-slate-100 hover:bg-emerald-50 cursor-pointer" onClick={() => go('form', 'txndetail', { txnId: t.txn_id })} data-testid={`advsearch-row-${t.txn_id}`}>
                  <td className="p-2">{t.type}</td><td className="p-2">{t.num}</td><td className="p-2">{t.name}</td><td className="p-2">{fmtDate(t.date)}</td>
                  <td className="p-2 text-right tabular-nums">{money(t.amount)}</td>
                  <td className="p-2">{t.status && <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'Overdue' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{t.status}</span>}</td>
                </tr>
              ))}
              {res.transactions.length === 0 && <tr><td colSpan={6} className="p-4 text-slate-500">No transactions match your search.</td></tr>}
            </tbody></table>
        </div>
      )}
    </div>
  );
}

// ── 1099s ────────────────────────────────────────────────────────────────────
export function Nine99Page({ base }) {
  const [tab, setTab] = useState('recipients');
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    axios.get(`${base}/reports/1099-summary`, { params: { tax_year: year } }).then((r) => setSummary(r.data)).catch(() => setSummary({ rows: [] }));
    axios.get(`${base}/vendors?scope=all`).then((r) => setVendors((r.data || []).filter((v) => v.is_1099))).catch(() => setVendors([]));
  }, [base, year]);
  const byId = Object.fromEntries((summary?.rows || []).map((r) => [r.vendor_id, r]));
  const dl = (fmt) => window.open(`${base}/reports/1099-export?tax_year=${year}&fmt=${fmt}`, '_blank');
  const years = []; for (let y = new Date().getFullYear(); y >= new Date().getFullYear() - 5; y--) years.push(y);
  return (
    <div className="space-y-4 text-slate-800" data-testid="nine99-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">1099s</h2>
        <div className="flex items-center gap-2">
          <select className={field + ' w-28'} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="nine99-year">{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
          <Button variant="outline" onClick={() => dl('csv')} data-testid="nine99-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" onClick={() => dl('pdf')} data-testid="nine99-pdf"><Download className="h-4 w-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={() => window.open(`${base}/reports/1099-forms?tax_year=${year}&form=nec`, '_blank')} data-testid="nine99-forms" title="Recipient copies (Copy B) on plain paper"><FileText className="h-4 w-4 mr-1" />1099-NEC forms</Button>
          <Button variant="outline" onClick={() => window.open(`${base}/reports/1099-forms?tax_year=${year}&form=1096`, '_blank')} data-testid="nine99-1096" title="1096 summary worksheet"><FileText className="h-4 w-4 mr-1" />1096</Button>
        </div>
      </div>
      <div className="flex gap-1 border-b border-slate-200">
        {[['recipients', 'Recipients & W-9s'], ['completed', 'Completed forms']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`nine99-tab-${k}`} className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-emerald-600 text-emerald-700 font-medium' : 'border-transparent text-slate-500'}`}>{l}</button>
        ))}
      </div>
      {tab === 'recipients' && (
        <div className={`rounded-lg border overflow-hidden ${CARD}`} data-testid="nine99-recipients">
          <table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="p-2">Name</th><th className="p-2 text-center">TIN on file</th><th className="p-2">W-9 status</th><th className="p-2">Address</th><th className="p-2 text-right">Total paid ({year})</th></tr></thead>
            <tbody>
              {vendors.map((v) => { const s = byId[v.name_id]; const hasTin = !!(v.tax_id_last4 || v.tax_id_masked); return (
                <tr key={v.name_id} className="border-b border-slate-100" data-testid={`nine99-recip-${v.name_id}`}>
                  <td className="p-2">{v.name}</td>
                  <td className="p-2 text-center">{hasTin ? <span className="text-emerald-700">Yes</span> : <span className="text-amber-700">No</span>}</td>
                  <td className="p-2">{hasTin ? 'On file' : 'Requested'}</td>
                  <td className="p-2 text-slate-500">{(s?.address) || v.address || '—'}</td>
                  <td className="p-2 text-right tabular-nums">{money(s?.total || 0)}</td>
                </tr>
              ); })}
              {vendors.length === 0 && <tr><td colSpan={5} className="p-4 text-slate-500">No contractors flagged for 1099. Flag a vendor on the Contractors page.</td></tr>}
            </tbody></table>
        </div>
      )}
      {tab === 'completed' && (
        <div className={`rounded-lg border overflow-hidden ${CARD}`} data-testid="nine99-completed">
          <table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="p-2">Name</th><th className="p-2">Boxes</th><th className="p-2 text-right">Total</th><th className="p-2">Meets threshold</th></tr></thead>
            <tbody>
              {(summary?.rows || []).map((r) => (
                <tr key={r.vendor_id} className="border-b border-slate-100" data-testid={`nine99-done-${r.vendor_id}`}>
                  <td className="p-2">{r.name}</td>
                  <td className="p-2 text-slate-500">{Object.keys(r.boxes || {}).join(', ') || '—'}</td>
                  <td className="p-2 text-right tabular-nums">{money(r.total)}</td>
                  <td className="p-2">{r.meets_threshold ? <span className="text-emerald-700">Yes</span> : <span className="text-slate-400">No</span>}</td>
                </tr>
              ))}
              {(summary?.rows || []).length === 0 && <tr><td colSpan={4} className="p-4 text-slate-500">No 1099 totals for {year}.</td></tr>}
            </tbody></table>
        </div>
      )}
    </div>
  );
}

// ── Custom form styles ────────────────────────────────────────────────────────
export function FormStylesPage({ base }) {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState('');
  useEffect(() => { axios.get(`${base}/settings`).then((r) => setS(r.data?.form_style || {})).catch(() => setS({})); }, [base]);
  if (!s) return <div className="text-slate-500">Loading…</div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => { await axios.put(`${base}/settings`, { reports: {}, form_style: s }).catch(() => {}); setSaved('Saved'); setTimeout(() => setSaved(''), 1500); };
  const accent = s.accent || '#059669';
  return (
    <div className="space-y-4 text-slate-800" data-testid="form-styles-page">
      <div className="flex items-center justify-between"><h2 className="text-xl font-semibold flex items-center gap-2"><Palette className="h-5 w-5 text-emerald-600" />Custom form styles</h2>{saved && <span className="text-sm text-emerald-600">{saved}</span>}</div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className={`rounded-lg border p-4 ${CARD} space-y-3`}>
          <label className="grid grid-cols-[120px_1fr] items-center gap-2"><span className="text-sm text-slate-600">Logo URL</span><Input className={field} value={s.logo_url || ''} onChange={(e) => set('logo_url', e.target.value)} data-testid="formstyle-logo" /></label>
          <label className="grid grid-cols-[120px_1fr] items-center gap-2"><span className="text-sm text-slate-600">Accent color</span><input type="color" value={accent} onChange={(e) => set('accent', e.target.value)} data-testid="formstyle-accent" className="h-9 w-16 rounded border border-slate-300" /></label>
          <label className="grid grid-cols-[120px_1fr] items-center gap-2"><span className="text-sm text-slate-600">Footer text</span><Input className={field} value={s.footer || ''} onChange={(e) => set('footer', e.target.value)} data-testid="formstyle-footer" /></label>
          <Button className="bg-emerald-600 text-white" onClick={save} data-testid="formstyle-save">Save</Button>
        </div>
        <div className={`rounded-lg border p-4 ${CARD}`} data-testid="formstyle-preview">
          <div className="text-xs uppercase text-slate-400 mb-2">Preview</div>
          <div className="border border-slate-200 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              {s.logo_url ? <img src={s.logo_url} alt="logo" className="h-10" /> : <div className="h-10 w-24 bg-slate-100 rounded" />}
              <div className="text-lg font-bold" style={{ color: accent }}>INVOICE</div>
            </div>
            <div className="h-1 rounded mb-3" style={{ background: accent }} />
            <div className="text-sm text-slate-600">Line items appear here…</div>
            <div className="mt-6 text-xs text-slate-400 border-t border-slate-200 pt-2">{s.footer || 'Thank you for your business.'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Custom fields ──────────────────────────────────────────────────────────────
export function CustomFieldsPage({ base }) {
  const [fields, setFields] = useState([]);
  const [name, setName] = useState('');
  const [where, setWhere] = useState('sales');
  const [saved, setSaved] = useState('');
  useEffect(() => { axios.get(`${base}/settings`).then((r) => setFields((r.data?.custom_fields) || [])).catch(() => setFields([])); }, [base]);
  const persist = async (next) => { setFields(next); await axios.put(`${base}/settings`, { custom_fields: next }).catch(() => {}); setSaved('Saved'); setTimeout(() => setSaved(''), 1200); };
  const add = () => { if (!name.trim()) return; persist([...fields, { id: Math.random().toString(36).slice(2, 8), name: name.trim(), applies_to: where }]); setName(''); };
  const remove = (id) => persist(fields.filter((f) => f.id !== id));
  return (
    <div className="space-y-4 text-slate-800" data-testid="custom-fields-page">
      <div className="flex items-center justify-between"><h2 className="text-xl font-semibold flex items-center gap-2"><Tag className="h-5 w-5 text-emerald-600" />Custom fields</h2>{saved && <span className="text-sm text-emerald-600">{saved}</span>}</div>
      <div className={`rounded-lg border p-4 ${CARD} flex flex-wrap items-end gap-2 max-w-2xl`}>
        <label className="text-xs text-slate-500 flex-1">Field name<Input className={field} value={name} onChange={(e) => setName(e.target.value)} data-testid="customfield-name" /></label>
        <label className="text-xs text-slate-500">Appears on<select className={field} value={where} onChange={(e) => setWhere(e.target.value)} data-testid="customfield-where">{[['sales', 'Sales forms'], ['expenses', 'Expense forms'], ['all', 'All forms']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        <Button className="bg-emerald-600 text-white" onClick={add} data-testid="customfield-add">Add field</Button>
      </div>
      <div className={`rounded-lg border overflow-hidden ${CARD} max-w-2xl`}>
        <table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-2">Field</th><th className="p-2">Appears on</th><th className="p-2" /></tr></thead>
          <tbody>
            {fields.map((f) => (
              <tr key={f.id} className="border-b border-slate-100" data-testid={`customfield-row-${f.id}`}><td className="p-2">{f.name}</td><td className="p-2 text-slate-500">{f.applies_to}</td>
                <td className="p-2 text-right"><button className="text-rose-500 text-xs" onClick={() => remove(f.id)} data-testid={`customfield-del-${f.id}`}>Remove</button></td></tr>
            ))}
            {fields.length === 0 && <tr><td colSpan={3} className="p-4 text-slate-500">No custom fields yet.</td></tr>}
          </tbody></table>
      </div>
    </div>
  );
}

// ── Attachments (company files) ────────────────────────────────────────────────
export function AttachmentsPage({ base }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${base}/receipts`).then((r) => setRows(r.data?.rows || r.data || [])).catch(() => setRows([])); }, [base]);
  if (rows === null) return <div className="text-slate-500">Loading…</div>;
  return (
    <div className="space-y-4 text-slate-800" data-testid="attachments-page">
      <h2 className="text-xl font-semibold flex items-center gap-2"><Paperclip className="h-5 w-5 text-emerald-600" />Attachments</h2>
      <p className="text-sm text-slate-500">Files uploaded to OL Finance. Upload new files from the Receipts page.</p>
      <div className={`rounded-lg border overflow-hidden ${CARD}`}>
        <table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-2">File</th><th className="p-2">Date</th><th className="p-2 text-right">Amount</th><th className="p-2" /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.receipt_id || r.id} className="border-b border-slate-100" data-testid={`attachment-row-${r.receipt_id || r.id}`}>
                <td className="p-2 flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" />{r.filename || r.name}</td>
                <td className="p-2">{fmtDate(r.date)}</td>
                <td className="p-2 text-right tabular-nums">{r.amount != null ? money(r.amount) : '—'}</td>
                <td className="p-2 text-right"><a className="text-emerald-700 text-xs hover:underline" href={`${base}/receipts/${r.receipt_id || r.id}/file`} target="_blank" rel="noreferrer">Open</a></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-4 text-slate-500">No attachments yet.</td></tr>}
          </tbody></table>
      </div>
    </div>
  );
}
